import { fetchWithAuth } from "./FetchWithAuth";
import { url } from "./url";

const base = `${String(url).replace(/\/+$/, "")}/payments`;

const PAYMENT_SETTINGS_LS_KEY = "ems_payments_settings_v1";

export function readStoredPaymentSettings() {
  try {
    const raw = localStorage.getItem(PAYMENT_SETTINGS_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function writeStoredPaymentSettings(data) {
  if (!data || typeof data !== "object") return;
  try {
    localStorage.setItem(PAYMENT_SETTINGS_LS_KEY, JSON.stringify(data));
  } catch (_) {}
}

async function request(path, options = {}) {
  const endpoint = `${base}${path}`;
  const response = await fetchWithAuth(endpoint, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.error || `Request failed ${response.status}`);
  }
  return response.json();
}

async function requestForm(path, formData) {
  const endpoint = `${base}${path}`;
  const response = await fetchWithAuth(endpoint, {
    method: "POST",
    credentials: "include",
    body: formData,
    headers: {}, // let browser set multipart boundary
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.error || `Upload failed ${response.status}`);
  }
  return response.json();
}

export const paymentsApi = {
  shouldPromptPlan: (enquiryId) =>
    request(`/should-prompt-plan?enquiry_id=${encodeURIComponent(enquiryId)}`),

  createPlan: (payload) =>
    request("/plans", { method: "POST", body: JSON.stringify(payload) }),
  getPlan: (id) => request(`/plans/${id}`),
  getPlanByEnquiry: (enquiryId) => request(`/plans/enquiry/${enquiryId}`),

  createStages: (payload) =>
    request("/stages", { method: "POST", body: JSON.stringify(payload) }),
  updateStage: (id, payload) =>
    request(`/stages/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  listStages: (planId) => request(`/stages/plan/${planId}`),

  recordTransaction: (payload, options = {}) =>
    request("/transactions", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: options.idempotencyKey
        ? { "X-Idempotency-Key": options.idempotencyKey }
        : undefined,
    }),
  listTransactionsByEnquiry: (enquiryId) =>
    request(`/transactions/enquiry/${enquiryId}`),

  outstanding: (filter) =>
    request(filter ? `/outstanding?filter=${encodeURIComponent(filter)}` : "/outstanding"),
  metrics: () => request("/metrics"),
  accountsList: (filter, params = {}) => {
    const sp = new URLSearchParams();
    if (filter) sp.set("filter", filter);
    if (params.page != null) sp.set("page", String(params.page));
    if (params.page_size != null) sp.set("page_size", String(params.page_size));
    if (params.search) sp.set("search", params.search);
    if (params.enquiry_id) sp.set("enquiry_id", String(params.enquiry_id));
    if (params.student_name) sp.set("student_name", params.student_name);
    if (params.course) sp.set("course", params.course);
    if (params.status) sp.set("status", params.status);
    if (params.due_from) sp.set("due_from", params.due_from);
    if (params.due_to) sp.set("due_to", params.due_to);
    const q = sp.toString();
    return request(q ? `/accounts/list?${q}` : "/accounts/list");
  },

  uploadReceipt: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return requestForm("/receipt/upload", formData);
  },

  getSettings: async (opts = {}) => {
    const bypassCache = opts.bypassCache || process.env.NODE_ENV === "development";
    if (!bypassCache) {
      const cached = readStoredPaymentSettings();
      if (cached) return cached;
    }
    const data = await request("/settings");
    writeStoredPaymentSettings(data);
    return data;
  },
  updateSettings: (payload) =>
    request("/settings", { method: "PUT", body: JSON.stringify(payload) }),
};
